/**
 * Colour recommendations on the product page — sections/color-recommendations.liquid
 *
 * Fetches a collection filtered down to the product's own category and the colour
 * group of the variant on screen, and lifts the cards out of the response. The
 * markup comes from `templates/collection.empfehlung.liquid`, which renders
 * `sections/color-recommendations-cards.liquid` with `layout none`.
 *
 * Two things it refuses to do: show a half-empty row (below `data-min` cards in
 * the colour it tops the row up with the rest of the category, colour first) and
 * recommend the product a customer is already looking at (the product and its
 * Bezug siblings are removed from every response).
 *
 * Products pinned on the product (`custom.zuerst_empfohlen`) lead the row in
 * their order, each in the colour on screen. Their cards come in one request: the
 * cards section rendered on this product's own URL with `?variant=` for the colour
 * (`data-pinned-url`, `data-farbe-variants`).
 */
// Own cards a row keeps behind its pinned ones, however many are pinned.
const OWN_CARDS_MIN = 2;

class ColorRecommendations extends HTMLElement {
  #controller;
  #groups = {};
  #excluded = new Set();
  #group = '';
  #value = '';
  #loadController;
  #cache = new Map();
  #loaded = false;
  #pinnedUrl = '';
  #farbeVariants = {};
  #variantId = '';

  connectedCallback() {
    this.#controller = new AbortController();
    const { signal } = this.#controller;

    this.#groups = this.#json(this.dataset.groups, {});
    this.#excluded = new Set(this.#json(this.dataset.exclude, []));
    this.#group = this.dataset.group || '';
    this.#value = this.dataset.value || '';
    this.#pinnedUrl = this.dataset.pinnedUrl || '';
    this.#farbeVariants = this.#json(this.dataset.farbeVariants, {});
    this.#variantId = this.dataset.variantId || '';

    // The row is fetched as soon as the page falls idle, not when it scrolls into
    // view. Waiting for the intersection meant the request started at the moment
    // the customer arrived at the section, so the cards dropped in under their
    // eyes and pushed everything below them down. Idle time is late enough to
    // stay out of the product images' way (this row is never the LCP element)
    // and early enough that the fetch is long finished by the time anyone
    // scrolls this far.
    const start = () => {
      if (this.#loaded) return;
      observer.disconnect();
      this.#load();
    };

    // Kept as the trigger for browsers without requestIdleCallback (Safari below
    // 16.4), and as the safety net if the callback is starved past its timeout.
    const observer = new IntersectionObserver((entries) => {
      if (entries[0].isIntersecting) start();
    }, { rootMargin: '0px 0px 400px 0px' });
    observer.observe(this);
    signal.addEventListener('abort', () => observer.disconnect());

    if ('requestIdleCallback' in window) {
      const idle = requestIdleCallback(start, { timeout: 2000 });
      signal.addEventListener('abort', () => cancelIdleCallback(idle));
    }

    const picker = document.querySelector('hdt-variant-picker');
    const form = picker && document.forms[picker.getAttribute('form')];
    form?.addEventListener('product:updateNow', (event) => this.#onVariantChange(event), { signal });
  }

  disconnectedCallback() {
    this.#controller?.abort();
    this.#loadController?.abort();
  }

  #json(value, fallback) {
    if (!value) return fallback;
    try {
      return JSON.parse(value);
    } catch {
      return fallback;
    }
  }

  /**
   * The Farbe value carried by the event, whichever shape arrived.
   *
   * `product:updateNow` does not always carry a variant. When the customer picks
   * the first option before the others, the variant picker dispatches a stand-in
   * object holding nothing but `option0_name` (global.js:3073) — it is dispatched
   * only for the first fieldset, so it is the colour only when Farbe is option 1.
   *
   * @returns {string} empty when the event carries no readable colour
   */
  #farbeValue(variant) {
    const position = Number(this.dataset.farbePosition || 0);
    if (position === 1 && typeof variant.option0_name === 'string') return variant.option0_name;
    if (!position) return '';

    const value = variant[`option${position}`];
    return typeof value === 'string' ? value : '';
  }

  #onVariantChange(event) {
    const variant = event.detail?.variant;
    if (!variant) return;

    // An event we cannot read a colour out of must leave the state alone. Reading
    // the stand-in above as "no colour" used to clear it, and from that click on
    // the row silently dropped to category-only for the rest of the visit.
    const value = this.#farbeValue(variant);
    if (!value || value === this.#value) return;

    this.#value = value;
    this.#group = this.#groups[value] || '';
    // The pinned cards only follow the colour, so any variant in it will do;
    // the map covers the picker's stand-in event, which carries no id. With neither,
    // the request goes without `?variant=` rather than with the previous colour's.
    this.#variantId = String(this.#farbeVariants[value] || variant.id || '');
    if (this.#loaded) this.#load();
  }

  #url(farbe) {
    const url = new URL(this.dataset.collectionUrl, window.location.origin);
    url.searchParams.set('view', 'empfehlung');
    // `sort_by` is not only a preference: Shopify's robots.txt disallows
    // `/collections/*sort_by*`, and that is the only thing keeping these
    // head-less fragments out of the index. Always send it.
    url.searchParams.set('sort_by', this.dataset.sortBy || 'best-selling');

    const { categoryParam, categoryValue, farbeParam } = this.dataset;
    if (categoryParam && categoryValue) url.searchParams.set(categoryParam, categoryValue);
    if (farbe && farbeParam) url.searchParams.set(farbeParam, farbe);

    return url.toString();
  }

  // Cards are told apart by product id, never by the handle in their URL: on /pl-pl the
  // URL carries the translated handle, while `data-exclude` comes from the split
  // metafield, which only knows the product's gid and German handle.
  #productId(slide) {
    return slide.dataset.productId || '';
  }

  /**
   * @returns {Element|null} the cards wrapper, siblings of the current product removed
   *
   * Responses are cached per URL and handed out as clones: a customer stepping
   * through colours and back re-uses the parse instead of paying for it again,
   * and the caller is free to move the slides around without spoiling the cache.
   */
  async #fetchCards(farbe, signal) {
    const url = this.#url(farbe);
    // Diagnostics: both requests are kept, because a mixed-colour row and an
    // ignored colour filter look identical from the outside. Keeping only the
    // last one hid the colour request behind the top-up that followed it.
    this.dataset[farbe ? 'colourUrl' : 'categoryUrl'] = url;

    const cached = this.#cache.get(url);
    if (cached) return cached.cloneNode(true);

    // Low priority because this now runs on every product page, scrolled to or
    // not: nothing here may compete with the images the customer is looking at.
    const response = await fetch(url, { signal, priority: 'low' });
    if (!response.ok) return null;

    const html = new DOMParser().parseFromString(await response.text(), 'text/html');
    const cards = html.querySelector('[data-color-recommendations-cards]');
    if (!cards) return null;

    for (const slide of cards.querySelectorAll('.hdt-slider__slide')) {
      if (this.#excluded.has(this.#productId(slide))) slide.remove();
    }

    this.#cache.set(url, cards);
    return cards.cloneNode(true);
  }

  /**
   * @returns {Element[]} the pinned cards in the colour on screen, cloned from cache
   *
   * The response depends on the colour alone, but it is keyed by variant: two
   * variants of one colour cost one extra request at most.
   */
  async #fetchPinned(signal) {
    if (!this.#pinnedUrl) return [];

    const url = new URL(this.#pinnedUrl, window.location.origin);
    if (this.#variantId) url.searchParams.set('variant', this.#variantId);
    url.searchParams.set('section_id', 'color-recommendations-cards');
    const key = url.toString();
    this.dataset.pinnedUrlFetched = key;

    let wrapper = this.#cache.get(key);
    if (!wrapper) {
      const response = await fetch(key, { signal, priority: 'low' });
      if (!response.ok) return [];

      const html = new DOMParser().parseFromString(await response.text(), 'text/html');
      wrapper = html.querySelector('[data-color-recommendations-pinned]');
      if (!wrapper) return [];
      this.#cache.set(key, wrapper);
    }
    return [...wrapper.cloneNode(true).querySelectorAll('.hdt-slider__slide')];
  }

  /**
   * Pinned cards go first in their own order. A pinned product the row already
   * carries is moved rather than shown twice. Behind them the row keeps its own
   * cards up to the length its shell was computed for, and never fewer than two:
   * the pinned ones lead a row, they are not a row of their own. A MIRA sofa pins
   * eight products, so its row grows past the usual eight.
   */
  #prependPinned(cards, pinnedSlides) {
    const track = cards?.querySelector('.hdt-slider__container');
    if (!track || !pinnedSlides.length) return;

    const before = this.#count(cards);
    const ids = new Set(pinnedSlides.map((slide) => this.#productId(slide)).filter(Boolean));
    for (const slide of [...track.querySelectorAll('.hdt-slider__slide')]) {
      if (ids.has(this.#productId(slide))) slide.remove();
    }

    const own = [...track.querySelectorAll('.hdt-slider__slide')];
    const keep = Math.max(before - pinnedSlides.length, Math.min(OWN_CARDS_MIN, own.length));
    for (const slide of own.slice(keep)) slide.remove();

    track.prepend(...pinnedSlides);
    if (this.#count(cards) > before) this.#activateSlider(cards);
  }

  /**
   * The shell's slider flags were computed in Liquid for its own card count; a row
   * that grew past it may now need the slider where the shell switched it off.
   * Only the tablet and desktop flags are raised: the mobile one is the merchant's
   * `swipe_on_mobile` choice.
   */
  #activateSlider(cards) {
    const slider = cards.querySelector('hdt-slider-reveal');
    if (!slider) return;

    const count = this.#count(cards);
    const columns = (prefix) => Number(slider.className.match(new RegExp(`${prefix}hdt-slides-(\\d+)`))?.[1] || 0);
    const config = this.#json(slider.getAttribute('config'), null);
    for (const [prefix, query] of [['md:', '(min-width: 768px)'], ['xl:', '(min-width: 1150px)']]) {
      if (count <= columns(prefix)) continue;
      slider.classList.replace(`${prefix}hdt-slider__active-false`, `${prefix}hdt-slider__active-true`);
      if (config?.breakpoints?.[query]) config.breakpoints[query].active = true;
    }
    if (config) slider.setAttribute('config', JSON.stringify(config));
  }

  #count(cards) {
    return cards ? cards.querySelectorAll('.hdt-slider__slide').length : 0;
  }

  /**
   * Colour cards first, the rest of the category behind them as filler.
   *
   * The shell comes from the category response: its slider config is computed for
   * a full row, while the colour response may have been laid out for two cards.
   * Any product both responses carry keeps its colour version — that one points at
   * the right variant, with the matching image and `?variant=` in the link.
   */
  #merge(colour, category) {
    if (!colour) return category;
    if (!category) return colour;

    const track = category.querySelector('.hdt-slider__container');
    if (!track) return colour;

    const limit = this.#count(category);
    const colourSlides = [...colour.querySelectorAll('.hdt-slider__slide')];
    const taken = new Set(colourSlides.map((slide) => this.#productId(slide)).filter(Boolean));

    for (const slide of [...track.querySelectorAll('.hdt-slider__slide')]) {
      if (taken.has(this.#productId(slide))) slide.remove();
    }
    track.prepend(...colourSlides);

    for (const slide of [...track.querySelectorAll('.hdt-slider__slide')].slice(limit)) {
      slide.remove();
    }

    return category;
  }

  async #load() {
    // A colour the merchant has grouped in Search & Discovery filters by its
    // group, so beige recommends the whole beige family. A colour with no group
    // still filters by its own value — narrower, but far better than dropping
    // the colour entirely. Never guess which group an ungrouped colour belongs to.
    const farbe = this.#group || this.#value;
    const min = Number(this.dataset.min || 4);

    this.#loadController?.abort();
    const { signal } = (this.#loadController = new AbortController());
    // Flipped on start, not on success: a colour picked while the first request
    // is still open has to supersede it rather than be swallowed.
    this.#loaded = true;

    try {
      // Started first and awaited last: the pinned requests run alongside the
      // row's. A failed one never costs the row, and an aborted one is settled
      // here so it cannot surface as an unhandled rejection when the row aborts first.
      const pinnedRequest = this.#fetchPinned(signal).catch(() => []);
      const colour = farbe ? await this.#fetchCards(farbe, signal) : null;
      const inColour = this.#count(colour);

      let cards = colour;
      if (inColour < min) {
        cards = this.#merge(colour, await this.#fetchCards('', signal));
      }

      const total = this.#count(cards);
      this.dataset.colourTier = this.#group ? 'group' : (this.#value ? 'value' : 'none');
      this.dataset.colourCount = String(inColour);
      this.dataset.categoryCount = String(total - inColour);

      if (signal.aborted) return;
      // An empty row stays hidden even with pinned products: they lead a row,
      // they are not a row of their own.
      if (!total) return this.#hide();

      const pinnedSlides = await pinnedRequest;
      // The catch above turns an abort into an empty list, so a superseded load would
      // otherwise carry on and paint the previous colour over the new one.
      if (signal.aborted) return;
      this.#prependPinned(cards, pinnedSlides);
      this.dataset.pinnedCount = String(pinnedSlides.length);

      this.replaceChildren(cards);
      this.#section?.removeAttribute('hidden');
      document.dispatchEvent(new CustomEvent('currencyUpdate'));
    } catch (error) {
      if (error.name === 'AbortError') return;
      console.error(error);
      this.#hide();
    }
  }

  get #section() {
    return this.closest('.hdt-section-recommened-product');
  }

  #hide() {
    this.replaceChildren();
    const section = this.#section;
    if (section) section.hidden = true;
  }
}

customElements.define('color-recommendations', ColorRecommendations);
